import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRole } from '../../utils/role';
import axiosInstance from '../../utils/axios';
import Wrapper from '../../components/Wrapper';
import { fetchPartners } from '../../store/features/partnersSlice';
import { uploadImageToS3, getFileExtension } from '../../utils/s3Upload'; 

const AddProductPage = () => {
  const dispatch = useDispatch();
  const role = getRole();
  const isSuperAdmin = role === 'SUPPORT';

  // Fetch partners from Redux store
  const partners = useSelector((state) => state.partners?.partners ?? []);
  const isLoadingPartners = useSelector((state) => state.partners?.isLoading ?? false);

  const initialFormState = {
    company: '',
    product_name: '',
    product_details: '',
    technical: '',
    admin_id: '', // Compulsory for Super Admin
  };

  const [formData, setFormData] = useState(initialFormState);
  const [packSizes, setPackSizes] = useState(['']); 
  
  // Image handling states
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchPartners({ page: 1, limit: 500 }));
    }
  }, [dispatch, isSuperAdmin]);

  // Clean up object URL to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit example
        setMessage({ type: 'danger', text: 'Image size should be less than 5MB.' });
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setMessage({ type: '', text: '' });
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    // Reset the file input value
    document.getElementById('productImageInput').value = '';
  };

  // Pack Sizes handlers
  const handlePackSizeChange = (index, value) => {
    const newPackSizes = [...packSizes];
    newPackSizes[index] = value;
    setPackSizes(newPackSizes);
  };

  const addPackSizeField = () => {
    if (packSizes.length < 30) setPackSizes([...packSizes, '']);
  };

  const removePackSizeField = (index) => {
    setPackSizes(packSizes.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (isSuperAdmin && !formData.admin_id) {
      return setMessage({ type: 'danger', text: 'Please select a partner from the dropdown.' });
    }

    const cleanPackSizes = packSizes.map(p => p.trim()).filter(p => p !== '');
    if (cleanPackSizes.length === 0) {
      return setMessage({ type: 'danger', text: 'Please add at least one valid pack size.' });
    }

    setIsSubmitting(true);

    try {
      // 1. Upload image to S3 if a file is selected
      let uploadedPhotoUrl = null;
      if (selectedFile) {
        const fileExt = getFileExtension(selectedFile);
        // Using 'product-images' as the S3 folder
        uploadedPhotoUrl = await uploadImageToS3(selectedFile, fileExt, 'product-images', 'soildoctor-images');
      }

      // 2. Prepare payload for the database
      const endpoint = isSuperAdmin ? '/super-admin/products' : '/admin/products';
      
      const payload = {
        company: formData.company || null,
        product_name: formData.product_name,
        product_details: formData.product_details || null,
        technical: formData.technical || null,
        photo_url: uploadedPhotoUrl, // Attach the S3 URL
        pack_sizes: cleanPackSizes,
      };

      if (isSuperAdmin) {
        payload.admin_id = formData.admin_id;
      }

      // 3. Save Product
      await axiosInstance.post(endpoint, payload);

      setMessage({ type: 'success', text: 'Product added successfully!' });
      
      // Reset form
      setFormData(initialFormState);
      setPackSizes(['']);
      removeImage();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add product. Please try again.';
      const errorCode = err.response?.data?.code;
      
      if (errorCode === 'DUPLICATE') {
        setMessage({ type: 'danger', text: `${errorMsg} (ID: ${err.response.data.product_id})` });
      } else {
        setMessage({ type: 'danger', text: errorMsg });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <h4 className="m-0 fw-bold">Add New Product</h4>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} py-2`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card shadow-sm border-0 mb-5">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            
            {isSuperAdmin && (
              <div className="row mb-4 pb-3 border-bottom">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Assign to Partner <span className="text-danger">*</span></label>
                  <select
                    name="admin_id"
                    className="form-select"
                    value={formData.admin_id}
                    onChange={handleInputChange}
                    disabled={isLoadingPartners}
                    required
                  >
                    <option value="" disabled>-- Select a Partner --</option>
                    {partners.map((partner) => (
                      <option key={partner.admin_id} value={partner.admin_id}>
                        {partner.entity_name}
                      </option>
                    ))}
                  </select>
                  <div className="form-text text-muted small">
                    This partner will manage this product in their portal.
                  </div>
                </div>
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Product Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="product_name"
                  className="form-control"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  maxLength="255"
                  required
                  placeholder="e.g. Super Action Mono Zinc"
                />
              </div>
              <div className="col-md-6 mt-3 mt-md-0">
                <label className="form-label fw-semibold">Company Name</label>
                <input
                  type="text"
                  name="company"
                  className="form-control"
                  value={formData.company}
                  onChange={handleInputChange}
                  maxLength="255"
                  placeholder="e.g. Dayal"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Product Details</label>
                <textarea
                  name="product_details"
                  className="form-control"
                  rows="3"
                  value={formData.product_details}
                  onChange={handleInputChange}
                  placeholder="e.g. Partially chelated micronutrient mixture"
                ></textarea>
              </div>
              <div className="col-md-6 mt-3 mt-md-0">
                <label className="form-label fw-semibold">Technical Specs</label>
                <textarea
                  name="technical"
                  className="form-control"
                  rows="3"
                  value={formData.technical}
                  onChange={handleInputChange}
                  placeholder="e.g. Zn: 33%, S: 15%"
                ></textarea>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Product Image (Optional)</label>
              <div className="d-flex align-items-start gap-3">
                {previewUrl && (
                  <div className="position-relative">
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="img-thumbnail" 
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                    />
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm position-absolute top-0 start-100 translate-middle rounded-circle p-1"
                      style={{ width: '24px', height: '24px', lineHeight: '1' }}
                      onClick={removeImage}
                    >
                      &times;
                    </button>
                  </div>
                )}
                <div className="flex-grow-1">
                  <input
                    type="file"
                    id="productImageInput"
                    className="form-control"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <div className="form-text text-muted small">
                    Max size: 5MB. Recommended square image for best display.
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            <div className="mb-4">
              <label className="form-label fw-semibold d-block">Pack Sizes <span className="text-danger">*</span></label>
              
              {packSizes.map((size, index) => (
                <div key={index} className="d-flex align-items-center mb-2" style={{ maxWidth: '400px' }}>
                  <input
                    type="text"
                    className="form-control me-2"
                    value={size}
                    onChange={(e) => handlePackSizeChange(index, e.target.value)}
                    placeholder="e.g. 5 kg, 250 ml"
                    maxLength="255"
                    required
                  />
                  {packSizes.length > 1 && (
                    <button 
                      type="button" 
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removePackSizeField(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              
              <button 
                type="button" 
                className="btn btn-sm btn-outline-primary mt-2" 
                onClick={addPackSizeField}
                disabled={packSizes.length >= 30}
              >
                + Add Another Pack Size
              </button>
            </div>

            <div className="d-flex justify-content-end border-top pt-3 mt-4">
              <button 
                type="submit" 
                className="btn btn-primary px-4" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Saving Product...</>
                ) : (
                  'Save Product'
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </Wrapper>
  );
};

export default AddProductPage;